import { Component, OnInit, ViewChild } from '@angular/core';
import {
  PoNotificationService,
  PoTableAction,
  PoModalComponent,
} from '@po-ui/ng-components';
import { Router } from '@angular/router';
import { AdmOrcamentoListService } from './adm_orcamento-list.service';
import { switchMap, of, map, catchError, forkJoin } from 'rxjs';
import { OrcamentoItem, OrcamentoListItem } from './orcamento.interface';

@Component({
  selector: 'app-adm_orcamento-list',
  templateUrl: './adm_orcamento-list.component.html',
  styleUrls: ['./adm_orcamento-list.component.css'],
  standalone: false,
})
export class AdmOrcamentoListComponent implements OnInit {

  constructor(
    private orcamentoListService: AdmOrcamentoListService,
    private router: Router,
    private poNotification: PoNotificationService
  ) { }

  orcamentoList: OrcamentoListItem[] = [];
  orcamento: OrcamentoListItem[] = [];
  nome: string = '';
  bOrcamento: string = '';
  dialogMethod: string = '';
  columns: Array<any> = [];
  regraIntegracao: boolean = true;
  public loading: boolean = true;

  @ViewChild(PoModalComponent, { static: true }) poModal!: PoModalComponent;

  actions: Array<PoTableAction> = [
    {
      action: this.printOrcamento.bind(this),
      icon: "po-icon po-icon-pdf",
      label: "Visualizar/Imprimir",
    }
  ];

  ngOnInit(): void {
    this.updateOrcamentoList();
    this.columns = this.orcamentoListService.getColumns();
    this.loading = false;
    this.dialogMethod = '';
  }

  viewOrcamento(orcamento: OrcamentoListItem): void {
    const id = orcamento.numOrcamento;
    this.router.navigate([`orcamento-view/${id}`]);
  }

  printOrcamento(orcamento: OrcamentoListItem): void {
    const id = orcamento.numOrcamento;
    this.router.navigate([`orcamento-print/${id}`]);
  }

  updateOrcamentoList(): void {
    this.loading = true;

    this.orcamentoListService.getOrcamentoList('').pipe(
      switchMap((response: OrcamentoItem[]) => {
        if (!response || response.length === 0) {
          return of([]);
        }

        const clientNameObservables = response.map(item =>
          this.orcamentoListService.getNomeCliente(item.cgc, item.segmto).pipe(
            map(nomeDoCliente => {
              let nome = '';
              if (Array.isArray(nomeDoCliente) && nomeDoCliente.length > 0 && nomeDoCliente[0]?.nome) {
                nome = nomeDoCliente[0].nome.trim();
              }
              return { originalItem: item, nome: nome };
            }),
            catchError(err => {
              console.error(`Erro ao obter nome do cliente para CGC: ${item.cgc}, Segmento: ${item.segmto}`, err);
              return of({ originalItem: item, nome: '' });
            })
          )
        );

        return forkJoin(clientNameObservables);
      }),
      map(results => {
        return results.map(result => ({
          numOrcamento: ("0000" + result.originalItem.id).slice(-4),
          numPedido: result.originalItem.numPedido,
          nome: result.nome,
          cgc: result.originalItem.cgc,
          emissao: result.originalItem.emissao ? result.originalItem.emissao.replace('Z', '') : '',
          status: result.originalItem.status,
          segmto: result.originalItem.segmto
        } as OrcamentoListItem));
      }),
      catchError(err => {
        console.error('Erro geral ao atualizar lista de orçamento:', err);
        this.poNotification.error('Não foi possível carregar a lista de orçamentos.');
        return of([]);
      })
    ).subscribe({
      next: (itemList: OrcamentoListItem[]) => {
        this.orcamentoList = itemList;
        this.orcamento = itemList;
        this.loading = false;
        console.log('Lista de orçamento atualizada com sucesso:', this.orcamentoList);
      },
      error: (err) => {
        console.error('Assinatura final da atualização da lista de orçamento falhou:', err);
        this.orcamentoList = [];
        this.orcamento = [];
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
        console.log('Processo de atualização da lista de orçamento concluído.');
      }
    });
  }

  buscaOrcamento(): void {
    if (!this.bOrcamento || this.bOrcamento.trim() === '') {
      this.poNotification.warning('Por favor, informe um número de orçamento para buscar.');
      return;
    }

    this.loading = true;

    this.orcamentoListService.getOrcamentoList(this.bOrcamento).pipe(
      switchMap((response: OrcamentoItem) => {
        if (!response) {
          return of(null);
        }

        return this.orcamentoListService.getNomeCliente(response.cgc, response.segmto).pipe(
          map(nomeDoCliente => {
            let nome = '';
            if (Array.isArray(nomeDoCliente) && nomeDoCliente.length > 0 && nomeDoCliente[0]?.nome) {
              nome = nomeDoCliente[0].nome.trim();
            }
            return { originalItem: response, nome: nome };
          }),
          catchError(err => {
            console.error(`Erro ao obter nome do cliente para CGC: ${response.cgc}, Segmento: ${response.segmto}`, err);
            return of({ originalItem: response, nome: '' });
          })
        );
      }),
      catchError(err => {
        console.error('Erro ao buscar orçamento:', err);
        this.poNotification.error('Não foi possível encontrar o orçamento.');
        return of(null);
      })
    ).subscribe({
      next: (result) => {
        this.orcamentoList = [];
        this.orcamento = [];

        if (result && result.originalItem) {
          const orcamentoItem: OrcamentoListItem = {
            numOrcamento: ("0000" + result.originalItem.id).slice(-4),
            numPedido: result.originalItem.numPedido,
            nome: result.nome,
            cgc: result.originalItem.cgc,
            emissao: result.originalItem.emissao ? result.originalItem.emissao.replace('Z', '') : '',
            status: result.originalItem.status,
            segmto: result.originalItem.segmto
          };

          this.orcamento.push(orcamentoItem);
          this.orcamentoList = [...this.orcamento];
        } else {
          this.poNotification.warning('Orçamento não encontrado.');
        }

        this.loading = false;
      },
      error: (err) => {
        console.error('Erro na busca do orçamento:', err);
        this.orcamentoList = [];
        this.orcamento = [];
        this.loading = false;
      }
    });
  }

  emailOrcamento(orcamento: OrcamentoListItem): void {
    if (!confirm("Confirma o envio do orçamento por E-mail?")) {
      return;
    }

    this.orcamentoListService.getNomeCliente(orcamento.cgc, orcamento.segmto).pipe(
      switchMap(nomeDoCliente => {
        if (!nomeDoCliente || !Array.isArray(nomeDoCliente) || nomeDoCliente.length === 0) {
          throw new Error('Cliente não encontrado');
        }

        const cliente = nomeDoCliente[0];
        if (!cliente.email) {
          throw new Error('E-mail do cliente não encontrado');
        }

        const emailCliente = cliente.email.trim() + ',' + 'atendimento@perfilloja.com.br';
        const codigoCliente = cliente.codigo ? cliente.codigo.trim() : '';

        return this.orcamentoListService.enviaEmail(emailCliente, orcamento.numOrcamento, codigoCliente);
      }),
      catchError(err => {
        console.error('Erro ao enviar email:', err);
        this.poNotification.error('Erro ao enviar e-mail. Verifique os dados do cliente.');
        return of(null);
      })
    ).subscribe({
      next: (emailResponse) => {
        if (emailResponse) {
          this.poNotification.setDefaultDuration(1500);
          this.poNotification.success("E-mail enviado com sucesso!");
        }
      },
      error: (err) => {
        console.error('Erro final no envio de email:', err);
        this.poNotification.error('Falha ao enviar e-mail.');
      }
    });
  }
}